import type { ApiErrorBody } from "./types";

/** Thrown by the API client for any non-2xx response (or a network failure, status 0). */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown>;

  constructor(status: number, code: string, message: string, details: Record<string, unknown> = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  static fromBody(status: number, body: unknown): ApiError {
    if (isErrorBody(body)) {
      const { code, message, details } = body.error;
      return new ApiError(status, code, message, details ?? {});
    }
    // FastAPI default validation errors: {"detail": [...]}
    if (body && typeof body === "object" && "detail" in body) {
      const detail = (body as { detail: unknown }).detail;
      const msg = typeof detail === "string" ? detail : "Request validation failed";
      return new ApiError(status, status === 422 ? "validation_error" : "http_error", msg, {
        detail,
      });
    }
    return new ApiError(status, "http_error", `Request failed (${status})`);
  }
}

function isErrorBody(body: unknown): body is ApiErrorBody {
  return (
    !!body &&
    typeof body === "object" &&
    "error" in body &&
    typeof (body as ApiErrorBody).error?.message === "string"
  );
}

/** Human-readable message for any thrown value. */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "Something went wrong";
}
